/**
 * GroupSetsTracker
 *
 * Tracker de execução pra um GRUPO de exercícios (bi-set/superset/circuito
 * — ver group_id em app/models/exercise.py). Diferente de
 * WorkoutSetsTracker (1 exercício, séries em sequência com descanso após
 * CADA série), aqui os exercícios do grupo se alternam por RODADA sem
 * descanso entre eles — o descanso só acontece depois que TODOS os
 * exercícios do grupo completam a série da rodada atual.
 *
 * Fluxo (bi-set de 2 exercícios, A e B):
 *   série 1 de A → série 1 de B (sem descanso) → DESCANSA → série 2 de A →
 *   série 2 de B → DESCANSA → ... até esgotar as séries.
 *
 * Se os itens do grupo tiverem sets_planned DIFERENTES (não é o uso
 * recomendado, mas o dado permite), o item que termina primeiro sai do
 * rodízio (onItemComplete já disparado pra ele) e os restantes continuam
 * se revezando — degrada graciosamente pro comportamento de
 * WorkoutSetsTracker normal quando sobra só 1 exercício ativo no grupo.
 *
 * Props:
 *   items                 – array de UserWorkoutPlanItem do grupo (mesma
 *                            group_id), na ordem do plano
 *   setsDataByItemId      – { [itemId]: [{set,reps,weight,done,...}] }
 *   doneMap               – { [itemId]: truthy } — vem de
 *                            workoutSession.exerciseCompletions
 *   onSetUpdate           – (itemId, setIndex, patch) => void
 *   onItemComplete        – (item, finalSets) => void — dispara
 *                            completeExercise pra ESTE item específico
 *   onRestStart           – (seconds, label, itemIds[]) => void
 *   onRestEnd             – () => void
 *   isResting             – bool — este GRUPO está em descanso agora
 *   previousSetsByItemId  – { [itemId]: previousSets }
 *   onError, onSuccess
 *   exerciseIndex, totalExercises, completedSoFar — pra numeração do toast
 *     ("Exercício X de Y finalizado"), usando o índice do PRIMEIRO item do
 *     grupo como referência.
 */
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLink } from "@fortawesome/free-solid-svg-icons";
import styles from "./WorkoutSetsTracker.module.css";
import {
  RestTimer,
  SetRow,
  TimedSetRow,
  isTimedExercise,
  getTimedSeconds,
  DEFAULT_REST_SECONDS,
} from "./WorkoutSetsTracker";

function getRestSecondsForItem(item, roundIndex) {
  const raw = (item?.rest_seconds || "").toString().trim();
  const parts = raw.split(",").map((s) => parseFloat(s.trim())).filter((v) => !isNaN(v) && v > 0);
  return parts.length > 0
    ? (parts[roundIndex] ?? parts[parts.length - 1] ?? DEFAULT_REST_SECONDS)
    : DEFAULT_REST_SECONDS;
}

function exerciseLabel(item) {
  return item?.exercise_item?.name || item?.name || "Exercício";
}

// Rótulo do grupo — mesma regra do professor (WorkoutAdminModal.jsx) e do
// card do aluno (Dashboard.js): 3+ exercícios = Circuito; exatamente 2 =
// Bi-set se for o MESMO exercise_id (mesmo grupo muscular) ou Superset se
// forem grupos diferentes. Usado no rótulo passado pro banner de descanso
// do header (onRestStart).
function groupTypeLabel(groupItems) {
  const size = groupItems?.length ?? 0;
  if (size >= 3) return "Circuito";
  if (size === 2) {
    const [a, b] = groupItems;
    const sameMuscleGroup = a?.exercise_id != null && a.exercise_id === b?.exercise_id;
    return sameMuscleGroup ? "Bi-set" : "Superset";
  }
  return "Bi-set";
}

export default function GroupSetsTracker({
  items,
  setsDataByItemId,
  doneMap,
  onSetUpdate,
  onItemComplete,
  onRestStart,
  onRestEnd,
  isResting,
  previousSetsByItemId,
  onError,
  onSuccess,
  exerciseIndex,
  totalExercises,
  completedSoFar,
}) {
  const [restingAfterRound, setRestingAfterRound] = useState(null);
  const pendingRestSecondsRef = useRef(DEFAULT_REST_SECONDS);
  const prevIsRestingRef = useRef(isResting);
  const trackerRef = useRef(null);

  // Header "Pular descanso" clicado externamente — mesmo padrão do
  // WorkoutSetsTracker (isResting vai true → false sem passar por
  // handleRestDone local).
  useEffect(() => {
    if (prevIsRestingRef.current && !isResting && restingAfterRound !== null) {
      setRestingAfterRound(null);
    }
    prevIsRestingRef.current = isResting;
  }, [isResting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Itens ainda com séries pendentes (fora do rodízio quem já completou
  // todas as suas séries — ver docstring do componente).
  const pendingItems = items.filter((it) => !doneMap?.[it.id]);

  // IMPORTANTE: quando o ÚLTIMO item pendente do grupo termina a ÚLTIMA
  // série da rodada, handleSubStepDone chama onItemComplete (marca esse
  // item como "done" no doneMap do pai) E setRestingAfterRound na MESMA
  // função — as duas atualizações batcham no mesmo render. Isso faz
  // pendingItems ficar vazio JUSTAMENTE na rodada em que ainda precisamos
  // mostrar o descanso dentro do card. Se o early return não checasse
  // restingAfterRound aqui, ele "vencia a corrida" e mostrava "Grupo
  // concluído" no lugar do RestTimer — o descanso só aparecia no banner
  // do header (que é controlado à parte, via onRestStart/activeRest no
  // Dashboard.js), nunca dentro do card. Ver também o gate isDone &&
  // !isRestingThis em Dashboard.js, que evita desmontar este componente
  // enquanto o descanso da última rodada ainda está rolando.
  if (pendingItems.length === 0 && restingAfterRound === null) {
    // Grupo inteiro concluído (e fora do período de descanso) — o card já
    // deveria estar colapsado no Dashboard.js antes de chegar aqui, mas
    // mantém um fallback seguro.
    return (
      <div className={styles.doneWrap}>
        <div className={styles.doneRow}>
          <span className={styles.doneIcon}><FontAwesomeIcon icon={faCheck} /></span>
          <span className={styles.doneLabel}>Grupo concluído</span>
        </div>
      </div>
    );
  }

  const doneCounts = pendingItems.map((it) => (setsDataByItemId[it.id] || []).filter((s) => s.done).length);
  const roundIndex = Math.min(...doneCounts);
  const subStepIdx = pendingItems.findIndex(
    (it) => (setsDataByItemId[it.id] || []).filter((s) => s.done).length === roundIndex
  );
  const currentItem = pendingItems[subStepIdx];
  const currentSets = setsDataByItemId[currentItem?.id] || [];
  const isLastPendingInRound = subStepIdx === pendingItems.length - 1;

  function handleSubStepDone(item, data) {
    const sets = setsDataByItemId[item.id] || [];
    const patch = { ...data, done: true, completedAt: new Date().toISOString() };
    onSetUpdate(item.id, roundIndex, patch);
    const builtSets = sets.map((s, i) => (i === roundIndex ? { ...s, ...patch } : s));

    if (builtSets.every((s) => s.done)) {
      const idxForToast = (exerciseIndex ?? 0) + items.findIndex((it) => it.id === item.id);
      if (exerciseIndex !== undefined && totalExercises !== undefined && idxForToast < totalExercises - 1) {
        const doneCount = (completedSoFar ?? 0) + 1;
        if (onSuccess) onSuccess(`Exercício ${doneCount} de ${totalExercises} finalizado`);
      }
      onItemComplete(item, builtSets);
    }

    if (isLastPendingInRound) {
      setRestingAfterRound(roundIndex);
      const restSecs = getRestSecondsForItem(pendingItems[0], roundIndex);
      pendingRestSecondsRef.current = restSecs;
      // Achado #4 da auditoria UX: o banner de descanso do header mostrava só
      // o TIPO genérico ("Descanso - Bi-set"), nunca quais exercícios —
      // diferente do fluxo de exercício avulso, que sempre mostra o nome
      // real ("Descanso - Supino Reto"). Combina os dois: tipo + nomes.
      const label = `${groupTypeLabel(items)}: ${items.map((it) => exerciseLabel(it)).join(" + ")}`;
      if (onRestStart) onRestStart(restSecs, label, items.map((it) => it.id));
    }
    // Senão (ainda não é o último exercício pendente da rodada): nenhuma
    // ação extra — o próximo subStep já fica disponível automaticamente
    // no próximo render, sem descanso, porque subStepIdx é derivado do
    // próprio estado (doneCounts).
  }

  function handleRestDone() {
    setRestingAfterRound(null);
    if (onRestEnd) onRestEnd();
  }

  const totalRoundsHint = Math.max(...items.map((it) => (setsDataByItemId[it.id] || []).length));
  const overallDoneSets = items.reduce((acc, it) => acc + (setsDataByItemId[it.id] || []).filter((s) => s.done).length, 0);
  const overallTotalSets = items.reduce((acc, it) => acc + (setsDataByItemId[it.id] || []).length, 0);

  return (
    <div className={styles.tracker} ref={trackerRef}>
      <div className={styles.progress}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${overallTotalSets ? (overallDoneSets / overallTotalSets) * 100 : 0}%` }}
          />
        </div>
        <span className={styles.progressLabel}>
          Rodada {Math.min(roundIndex + 1, totalRoundsHint)}/{totalRoundsHint}
        </span>
      </div>

      {restingAfterRound !== null ? (
        <RestTimer seconds={pendingRestSecondsRef.current} onDone={handleRestDone} />
      ) : (
        <div className={styles.setTable}>
          {/* Achado #2 da revisão UX (06/09/2026): esta é a informação mais
              crítica do modo grupo (qual exercício está ativo agora, sem
              descanso pro próximo) e mudava de conteúdo a cada série
              concluída sem nenhum jeito de leitor de tela perceber, já que
              não há troca de foco nem navegação — só o texto do card muda.
              aria-live="polite" faz o leitor de tela anunciar a mudança
              (WCAG 4.1.3 Status Messages) sem interromper o que o usuário
              estiver fazendo. */}
          <div className={styles.groupCurrentLabel} aria-live="polite">
            <FontAwesomeIcon icon={faLink} />
            <span>{exerciseLabel(currentItem)}</span>
            <span className={styles.groupCurrentHint}>
              - {subStepIdx + 1}/{pendingItems.length} do grupo, sem descanso entre eles
            </span>
          </div>

          {/* key inclui item.id + rodada — sem isso o React reaproveita a
              MESMA instância de SetRow ao trocar de exercício dentro do
              grupo (só a posição no JSX muda, não o tipo de elemento), e o
              estado interno de reps/peso digitado (useState em SetRow)
              vazava de um exercício pro outro. */}
          {isTimedExercise(currentItem?.reps_planned) ? (
            <TimedSetRow
              key={`${currentItem?.id}-${roundIndex}`}
              setNum={roundIndex + 1}
              targetSeconds={getTimedSeconds(currentItem?.reps_planned, roundIndex)}
              done={false}
              onDone={(data) => handleSubStepDone(currentItem, data)}
            />
          ) : (
            (() => {
              const prevSets = previousSetsByItemId?.[currentItem?.id]?.sets;
              const prevSet = prevSets?.[roundIndex] ?? prevSets?.[prevSets?.length - 1];
              return (
                <SetRow
                  key={`${currentItem?.id}-${roundIndex}`}
                  setNum={roundIndex + 1}
                  repsDefault={currentSets[roundIndex]?.reps ?? currentItem?.reps_planned}
                  weightDefault={currentSets[roundIndex]?.weight ?? currentItem?.weight_planned}
                  done={false}
                  onDone={(data) => handleSubStepDone(currentItem, data)}
                  onError={onError}
                  prevReps={prevSet?.reps ?? null}
                  prevWeight={prevSet?.weight ?? null}
                  equipment={currentItem?.exercise_item?.equipment}
                  requireWeight={
                    currentItem?.exercise_item?.movement_type === "forca" &&
                    currentItem?.exercise_item?.equipment !== "peso_corporal"
                  }
                />
              );
            })()
          )}

          {/* Fila do resto do grupo nesta rodada — só indicativo, sem input */}
          {pendingItems.length > 1 && (
            <div className={styles.groupQueue}>
              {pendingItems.map((it, i) =>
                i === subStepIdx ? null : (
                  <div key={it.id} className={styles.groupQueueItem}>
                    {i < subStepIdx ? "✓ " : "- "}
                    {exerciseLabel(it)}
                    {i < subStepIdx ? " feito nesta rodada" : " (a seguir)"}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
